import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Canada-Kenya is the only live corridor. The rate comes from the API
// (live market rate less the cockpit FX markup - the same rate checkout
// uses), so it can no longer drift. No rate -> no estimate, never a guess.
const API_ORIGIN = process.env.REACT_APP_API_BASE_URL ?? '';
const CORRIDORS = [
  { flag: '🇰🇪', country: 'Kenya',    currency: 'KES' },
];

const FEATURES = [
  { icon: '⚡', title: 'Instant Delivery',    desc: 'Funds arrive via M-Pesa, bank transfer, or mobile money within minutes.',        color: 'from-amber-400 to-orange-500' },
  { icon: '🛡️', title: 'FINTRAC Licensed',   desc: 'Fully registered MSB #C10001588. Your money is protected under Canadian law.',   color: 'from-emerald-400 to-teal-500' },
  { icon: '📅', title: 'Send Now, Pay Later', desc: 'Up to $250 CAD credit. Send today, repay in 14 days.',           color: 'from-violet-400 to-purple-600' },
  { icon: '💱', title: 'Best Rates',          desc: 'Transparent pricing on the Canada-Kenya corridor. More coming soon.',       color: 'from-blue-400 to-cyan-500' },
];

const STEPS = [
  { n: '01', title: 'Create Account',  desc: 'Sign up free in under 2 minutes.' },
  { n: '02', title: 'Choose Amount',   desc: 'Enter how much to send and get a live quote.' },
  { n: '03', title: 'Pick Pay Method', desc: 'Interac, wallet balance, or SNPL credit.' },
  { n: '04', title: 'Money Delivered', desc: 'Recipient gets funds via M-Pesa within minutes.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('CA');
  const [activeCorridor, setActiveCorridor] = useState(0);
  const [sendAmount, setSendAmount] = useState('100');
  const [rates, setRates] = useState({});

  useEffect(() => {
    let alive = true;
    fetch(`${API_ORIGIN}/api/v2/rates/indicative?from=CAD&to=KES`)
      .then((r) => r.json())
      .then((d) => { if (alive && d.ok) setRates({ KES: d.rate }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActiveCorridor(p => (p + 1) % CORRIDORS.length), 2500);
    return () => clearInterval(t);
  }, []);

  const c = { ...CORRIDORS[activeCorridor], rate: rates[CORRIDORS[activeCorridor].currency] };
  const estimated = sendAmount && c.rate ? Math.floor(parseFloat(sendAmount) * c.rate).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif" }} className="min-h-screen bg-white">

      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-24">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="ZoomPay" style={{height: "100px", width: "auto"}} />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-50 transition">Login</button>
            <button onClick={() => navigate('/register')} className="text-sm font-semibold bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm">Sign Up Free</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 text-white">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-500 opacity-20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-20 w-72 h-72 bg-indigo-600 opacity-20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs font-semibold text-blue-200 mb-6 border border-white/10">
              <span className="w-2 h-2 bg-emerald-400 rounded-full" style={{animation:'pulse 2s infinite'}} />
              Live · CAD & USD to 8 African Currencies
            </div>
            <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4">
              Send Money to<br />
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Africa, Instantly</span>
            </h1>
            <p className="text-blue-100 text-lg leading-relaxed mb-8 max-w-md">
              Fast, secure remittances from Canada & USA. Send now and pay later with up to <strong className="text-white">$250 CAD credit</strong> — zero interest.
            </p>
            <div className="flex gap-3 mb-6">
              {['CA','US'].map(code => (
                <button key={code} onClick={() => setSelectedCountry(code)}
                  className={"flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border-2 transition " + (selectedCountry === code ? 'bg-white text-slate-900 border-white' : 'bg-white/10 text-white border-white/20 hover:bg-white/20')}>
                  {code === 'CA' ? '🇨🇦' : '🇺🇸'} From {code === 'CA' ? 'Canada' : 'USA'}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => { localStorage.setItem('userCountry', selectedCountry); navigate('/register'); }}
                className="bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold px-8 py-3.5 rounded-xl hover:opacity-90 transition shadow-lg text-sm">
                Get Started Free →
              </button>
              <button onClick={() => navigate('/login')}
                className="bg-white/10 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-white/20 transition text-sm">
                Login
              </button>
            </div>
          </div>

          {/* Live rate card */}
          <div className="hidden md:block">
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 shadow-2xl">
              <p className="text-blue-200 text-xs font-semibold mb-4 uppercase tracking-widest">Exchange Rate</p>
              <div className="bg-white/10 rounded-xl p-4 mb-3">
                <p className="text-xs text-blue-300 mb-1">You send</p>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedCountry === 'CA' ? '🇨🇦' : '🇺🇸'}</span>
                  <input type="number" value={sendAmount} onChange={e => setSendAmount(e.target.value)}
                    className="bg-transparent text-3xl font-black text-white w-28 outline-none" />
                  <span className="text-white/70 font-bold">{selectedCountry === 'CA' ? 'CAD' : 'USD'}</span>
                </div>
              </div>
              <div className="text-center my-2 text-amber-400 text-xl font-bold">↓</div>
              <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-xl p-4">
                <p className="text-xs text-emerald-300 mb-1">Recipient gets (approx.)</p>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{c.flag}</span>
                  <span className="text-3xl font-black text-white">{estimated}</span>
                  <span className="text-white/70 font-bold">{c.currency}</span>
                </div>
                <p className="text-xs text-emerald-300 mt-1">{c.country} · {c.rate ? `1 CAD = ${c.rate.toFixed(2)} KES` : 'Rate loading…'}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {CORRIDORS.map((cor, i) => (
                  <button key={cor.currency} onClick={() => setActiveCorridor(i)}
                    className={"flex items-center gap-1 px-2 py-1 rounded-lg transition text-sm font-semibold " + (activeCorridor === i ? 'bg-white/25 text-white' : 'hover:bg-white/10 opacity-60 text-white')}>
                    <span className="text-lg">{cor.flag}</span><span>{cor.currency}</span>
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-xs text-blue-300">
                <span>✅ No hidden fees</span><span>✅ Interac</span><span>✅ M-Pesa</span>
              </div>
              <button onClick={() => navigate('/login')}
                className="w-full mt-4 py-4 bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-xl rounded-xl shadow-lg transition transform hover:scale-105">
                SEND NOW →
              </button>
            </div>
          </div>
        </div>

        {/* Corridor strip */}
        <div className="border-t border-white/10 bg-white/5">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-2 overflow-x-auto">
            <span className="text-xs text-blue-300 font-semibold shrink-0 mr-2">SEND TO →</span>
            {CORRIDORS.map((cor, i) => (
              <div key={cor.currency} className={"flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold shrink-0 transition " + (activeCorridor === i ? 'bg-amber-400 text-slate-900' : 'bg-white/10 text-white/80')}>
                <span>{cor.flag}</span><span>{cor.country}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[['8','African Corridors'],['$250','SNPL Credit Limit'],['C10001588','FINTRAC MSB #'],['< 5 min','Delivery Speed']].map(([v,l]) => (
            <div key={l}><p className="text-2xl font-black">{v}</p><p className="text-blue-200 text-xs mt-0.5">{l}</p></div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Why Choose ZoomPay?</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Built for the African diaspora in Canada and the USA.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(f => (
              <div key={f.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition">
                <div className={"w-12 h-12 rounded-xl bg-gradient-to-br " + f.color + " flex items-center justify-center text-2xl mb-4"}>{f.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SNPL SPOTLIGHT */}
      <section className="py-20 bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-violet-500/30 border border-violet-400/30 px-3 py-1 rounded-full text-xs font-semibold text-violet-200 mb-6">✨ Exclusive Feature</div>
            <h2 className="text-4xl font-black mb-4 leading-tight">Send Now,<br /><span className="bg-gradient-to-r from-violet-300 to-pink-300 bg-clip-text text-transparent">Pay Later</span></h2>
            <p className="text-violet-100 text-lg leading-relaxed mb-6">Your family can't wait. Send up to <strong className="text-white">$250 CAD</strong> today and repay within 14 days — completely interest-free.</p>
            <ul className="space-y-3 mb-8">
              {['Recipient gets funds immediately','14-day deferred settlement','Zero interest, zero hidden fees','Credit limit grows with on-time payments'].map(item => (
                <li key={item} className="flex items-center gap-3 text-violet-100 text-sm">
                  <span className="w-5 h-5 bg-emerald-400 rounded-full flex items-center justify-center text-slate-900 text-xs font-bold shrink-0">✓</span>{item}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/register')} className="bg-gradient-to-r from-violet-400 to-pink-400 text-slate-900 font-bold px-8 py-3.5 rounded-xl hover:opacity-90 transition shadow-lg text-sm">Try SNPL — Apply Now →</button>
          </div>
          <div className="flex justify-center">
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 w-72 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm font-bold text-violet-200">SNPL Credit</span>
                <span className="text-xs bg-emerald-400/20 text-emerald-300 px-2 py-0.5 rounded-full font-semibold">Active</span>
              </div>
              <p className="text-xs text-violet-300 mb-1">Available Credit</p>
              <p className="text-4xl font-black text-white mb-1">$250.00</p>
              <p className="text-xs text-violet-300 mb-5">CAD · 14-day settlement</p>
              <div className="space-y-2 text-xs">
                {[['Limit','$250.00 CAD'],['Used','$0.00 CAD'],['Interest','0% — Free']].map(([k,v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-violet-300">{k}</span>
                    <span className={k === 'Interest' ? 'text-emerald-400 font-bold' : 'text-white font-semibold'}>{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-xs text-violet-300">💡 3 on-time payments → credit limit +$25</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-500">Send money in 4 simple steps</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map(s => (
              <div key={s.n}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-sm mb-4 shadow-md">{s.n}</div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-950 text-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-5xl mb-6">🌍</div>
          <h2 className="text-4xl font-black mb-4">Ready to Send?</h2>
          <p className="text-blue-200 text-lg mb-8">Join the African diaspora sending money faster, cheaper, and smarter.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/register')} className="bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold px-10 py-4 rounded-xl hover:opacity-90 transition shadow-lg text-sm">Create Free Account →</button>
            <button onClick={() => navigate('/login')} className="bg-white/10 border border-white/20 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/20 transition text-sm">Already have an account? Login</button>
          </div>
          <p className="text-blue-400 text-xs mt-6">FINTRAC MSB #C10001588 · Licensed in Canada · ZoomPay Systems & Technology Ltd.</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
              <span className="text-white font-black text-xs">Z</span>
            </div>
            <span className="font-semibold text-slate-300">ZoomPay</span>
            <span>· © 2026 ZoomPay Systems & Technology Ltd.</span>
          </div>
          <div className="flex gap-6"><span>Privacy Policy</span><span>Terms of Service</span><span>Support</span></div>
        </div>
      </footer>
    </div>
  );
}
